// Hooks for report charts. These mirror use-dashboard-charts.js but target the
// parallel `/report_charts` container, which reuses the same chart-type schema.
import { useContext } from 'react'
import { useQuery, useMutation } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

const getReportCharts = async (token, reportId) => {
  const url = `reports/${reportId}/report_charts`
  const data = await apiGet({ url, token })
  return data
}

export function useReportCharts({ reportId, options = {} }) {
  const token = useContext(AuthContext)
  const queryFn = () => getReportCharts(token, reportId)
  const queryKey = [`reports/${reportId}/report_charts`]
  return useQuery(queryKey, queryFn, { enabled: !!reportId, ...options })
}

const createReportChart = async (token, reportId, params) => {
  const url = `reports/${reportId}/report_charts`
  const objectName = 'report_chart'
  const data = await apiPost({ url, token, objectName, params })
  return data
}

const updateReportChart = async (token, id, params) => {
  const url = `report_charts/${id}`
  const objectName = 'report_chart'
  const data = await apiPatch({ url, token, objectName, params })
  return data
}

const destroyReportChart = async (token, id) => {
  const url = `report_charts/${id}`
  await apiDelete({ url, token })
}

const mutationOptions = (options = {}) => ({
  onSuccess: (data, vars, ctx) => options?.onSuccess?.(data, vars, ctx),
  onError: (err, vars, ctx) => options?.onError?.(err, vars, ctx)
})

export function useCreateReportChart({ reportId, options }) {
  const token = useContext(AuthContext)
  return useMutation((params) => createReportChart(token, reportId, params), mutationOptions(options))
}

export function useUpdateReportChart({ options }) {
  const token = useContext(AuthContext)
  return useMutation(({ id, ...params }) => updateReportChart(token, id, params), mutationOptions(options))
}

export function useDestroyReportChart({ id, options }) {
  const token = useContext(AuthContext)
  return useMutation(() => destroyReportChart(token, id), mutationOptions(options))
}

// Variant that accepts the chart id at mutation call-time (not hook creation time).
// Used when one hook instance needs to delete different charts (e.g. the AI assistant).
export function useDestroyReportChartById({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation((id) => destroyReportChart(token, id), mutationOptions(options))
}
